import { IDetailInfoDataHttp } from 'common/model/common.model';
import {
  DetailGroupInfo,
  DetailInfo,
  IDetailGroupInfo,
  IDetailInfo,
  IDetailValue,
  IRuleParemeters
} from 'common/model';
import { IAddendum } from 'common/model/addendum.model';

export interface IAttendanceContactAnswerHttp {
  detailData: Array<IDetailInfoDataHttp>;
  contactGuid: string;
  evideInputContentFormGuid: string;
  selectedVariantGuid?: string | null;
}

export interface IAttendanceContactSaveRequestHttp {
  detailData: Array<IDetailInfoDataHttp>;
  projectId: number;
  activitySessionGuid?: string;
  contactGuid?: string;
  evideInputContentFormGuid: string;
  encodedResponse?: string;
  selectedVariantGuid?: string | null;
}

export interface IAttendanceSaveRequestHttp {
  detailData: Array<IDetailInfoDataHttp>;
  attendanceContactAnswerInfos: IAttendanceContactAnswerHttp[];
  attendanceGuid?: string;
  evideInputContentFormGuid: string;
  encodedResponse?: string;
  // Conditional Form Groups: form-level variant chosen at fill time.
  // Per-contact variants live on each attendanceContactAnswerInfos[].selectedVariantGuid.
  selectedVariantGuid?: string | null;
}

// Shape mirrors EvideInputContentFormVariantInfo on the server.
export interface IAttendanceFormVariant {
  id: number;
  variantGuid: string;
  title: string;
  description: string;
  sortOrder: number;
  isDefault: boolean;
}

export interface IAttendanceResponseHttp {
  scheduleTitle: string;
  projectId: number;
  evideInputContentFormGuid: string;
  groupInfos: IDetailGroupInfo[];
  detailData: IDetailValue[];
  ruleParemeters: IRuleParemeters;
  variants?: IAttendanceFormVariant[];
  selectedVariantGuid?: string | null;
  // False when the API stripped the note answers for this viewer (sharing tier RecordOnly /
  // legacy HideNotes) — a stripped note is otherwise indistinguishable from an empty one.
  // Gates the read-only view's "Export PDF" button.
  notesVisible?: boolean;
  // Project setting (off by default): whether the "Export PDF" download is enabled at all.
  // The button needs BOTH this and notesVisible.
  pdfExportEnabled?: boolean;
  // Whether to show THIS viewer the session's Documents card. Resolved per request by the API
  // (project switch + role mapping + note visibility), so it is never stale.
  canViewDocuments?: boolean;
  // Duration record (date + duration, no times): View Session shows a header card instead of
  // the attendance list; the note follows as usual.
  isDuration?: boolean;
  sessionDate?: string | null;
  durationMinutes?: number | null;
  durationText?: string | null;
  // Activity type behind the session: false = one-to-one, true = group. The API sends it only while
  // SMS is on for the project; the SMS controls hide on a one-to-one session whose client is not opted in.
  isMultiSession?: boolean | null;
}

export interface IAttendanceDurationInfo {
  sessionDate: string | null; // yyyy-MM-dd
  durationText: string | null;
}

export class AttendanceInfo {
  constructor(
    public readonly scheduleTitle: string,
    public readonly programDetailValue: IDetailValue[],
    public readonly groupInfos: Array<DetailGroupInfo>,
    public readonly evideInputContentFormGuid: string,
    public readonly projectId: number,
    public readonly ruleParemeters: IRuleParemeters,
    public readonly variants: IAttendanceFormVariant[] = [],
    public readonly selectedVariantGuid: string | null = null,
    public readonly notesVisible: boolean = true,
    // Default false: PDF download is opt-in per project, so an older API response
    // without the flag keeps the button hidden.
    public readonly pdfExportEnabled: boolean = false,
    // Default false: an older API response without the flag keeps the Documents card hidden.
    public readonly canViewDocuments: boolean = false,
    // Set only for a duration record.
    public readonly duration: IAttendanceDurationInfo | null = null,
    // False = one-to-one. Null when the API did not send it (SMS off, or an older API): never one-to-one.
    public readonly isMultiSession: boolean | null = null
  ) {}

  public static fromJson(data: IAttendanceResponseHttp): AttendanceInfo {
    return new this(
      data.scheduleTitle,
      data.detailData,
      data.groupInfos.map((item) =>
        DetailGroupInfo.fromJson(item, data.ruleParemeters)
      ),
      data.evideInputContentFormGuid,
      data.projectId,
      data.ruleParemeters,
      data.variants ?? [],
      data.selectedVariantGuid ?? null,
      data.notesVisible ?? true,
      data.pdfExportEnabled ?? false,
      data.canViewDocuments ?? false,
      data.isDuration
        ? {
            sessionDate: data.sessionDate ?? null,
            durationText: data.durationText ?? null
          }
        : null,
      data.isMultiSession ?? null
    );
  }
}

export interface IAttendanceContactResponseHttp {
  projectId: number;
  formGuid: string;
  activitySessionGuid: string;
  attendanceContactInfos: IAttendanceContactInfo[];
  gridRows: IAttendanceGridRow[];
  inactiveContactGuids: string[];
  variants?: IAttendanceFormVariant[];
  // Clinical-note lock (one-to-one only, Restrict Clinical Records on): when true the note is
  // read-only and amendments are captured as dated addendums.
  isLocked?: boolean;
  addendums?: IAddendum[];
  // Session staff / admins only — the API refuses AddActivitySessionAddendum for anyone else,
  // so the add-amendment panel must stay hidden for them too.
  canAddAddendum?: boolean;
}

export interface IAttendanceContactInfo {
  evideInputContentGuid: string;
  shortTitle: string;
  gridOrder: number;
  filterVisible: boolean;
  detailInfo: IDetailInfo | null;
}

export interface IAttendanceGridRow {
  contactGuid: string;
  attendanceContactGridDatas: IAttendanceContactGridData[];
  ruleParemeters: IRuleParemeters;
  allocatedUsersSummary?: string | null;
  registrationStartDate?: string | null;
  // Variant the saved attendance answer was filled with. Null for new / legacy rows.
  selectedVariantGuid?: string | null;
}

export interface IAttendanceContactGridData {
  evideInputContentGuid: string;
  dataStringGridValue: string;
}

export class AttendanceContact {
  public detailInfo: DetailInfo;
  public gridOrder: number;

  constructor(
    public readonly evideInputContentGuid: string,
    public readonly dataStringGridValue: string
  ) {}

  public static fromJson(data: IAttendanceContactGridData) {
    return new this(data.evideInputContentGuid, data.dataStringGridValue);
  }
}

export class AttendanceGridRow {
  constructor(
    public readonly uuid: string,
    public readonly contactGuid: string,
    public readonly attendanceContacts: AttendanceContact[],
    public readonly ruleParemeters: IRuleParemeters,
    public readonly allocatedUsersSummary: string | null = null,
    public readonly registrationStartDate: string | null = null,
    public readonly selectedVariantGuid: string | null = null
  ) {}

  public static fromJson(data: IAttendanceGridRow) {
    return new this(
      `${DetailGroupInfo.KeyInitials}-${data.contactGuid}`,
      data.contactGuid,
      data.attendanceContactGridDatas.map((item) =>
        AttendanceContact.fromJson(item)
      ),
      data.ruleParemeters,
      data.allocatedUsersSummary ?? null,
      data.registrationStartDate ?? null,
      data.selectedVariantGuid ?? null
    );
  }
}

export class AttendanceContactDetail {
  constructor(
    public readonly projectId: number,
    public readonly formGuid: string,
    public readonly activitySessionGuid: string,
    public readonly attendanceContactInfos: AttendanceContactInfo[],
    public readonly gridRows: AttendanceGridRow[],
    public readonly inactiveContactGuids: string[],
    public readonly variants: IAttendanceFormVariant[] = [],
    public readonly isLocked: boolean = false,
    public readonly addendums: IAddendum[] = [],
    public readonly canAddAddendum: boolean = false
  ) {}

  public static fromJson(data: IAttendanceContactResponseHttp) {
    return new AttendanceContactDetail(
      data.projectId,
      data.formGuid,
      data.activitySessionGuid,
      data.attendanceContactInfos.map((item) =>
        AttendanceContactInfo.fromJson(item)
      ),
      data.gridRows.map((item) => AttendanceGridRow.fromJson(item)),
      data.inactiveContactGuids || [],
      data.variants ?? [],
      data.isLocked ?? false,
      data.addendums ?? [],
      data.canAddAddendum ?? false
    );
  }
}

export class AttendanceContactInfo {
  constructor(
    public readonly evideInputContentGuid: string,
    public readonly shortTitle: string,
    public readonly gridOrder: number,
    public readonly filterVisible: boolean,
    public readonly detailInfo: DetailInfo
  ) {}

  public static fromJson(data: IAttendanceContactInfo) {
    return new AttendanceContactInfo(
      data.evideInputContentGuid,
      data.shortTitle,
      data.gridOrder,
      data.filterVisible,
      data.detailInfo ? DetailInfo.fromJson(data.detailInfo) : null
    );
  }
}
